import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { TemplateIcon } from '../utils/iconResolver';
import { fetchWithOrg } from '../utils/api';

import {
  ArrowSquareOut,
  CheckCircle,
  Clock,
  DownloadSimple,
  Eye,
  EyeSlash,
  PencilSimple,
  Play,
  Robot,
  WarningCircle
} from '@phosphor-icons/react';
const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'text-[#718096]', bg: 'bg-[rgba(113,128,150,0.12)]', icon: PencilSimple },
  pending_review: { label: 'Pending Review', color: 'text-[#ECC94B]', bg: 'bg-[rgba(236,201,75,0.12)]', icon: Clock },
  approved: { label: 'Approved', color: 'text-[#48BB78]', bg: 'bg-[rgba(72,187,120,0.12)]', icon: CheckCircle },
  changes_requested: { label: 'Changes Requested', color: 'text-[#ED8936]', bg: 'bg-[rgba(237,137,54,0.12)]', icon: WarningCircle },
  rejected: { label: 'Rejected', color: 'text-[#FC8181]', bg: 'bg-[rgba(252,129,129,0.12)]', icon: EyeSlash },
};

const MyAutomations = () => {
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [autoRes, profRes] = await Promise.all([
          fetchWithOrg(`${BACKEND_URL}/api/creators/me/automations`),
          fetchWithOrg(`${BACKEND_URL}/api/creators/me/profile`),
        ]);
        if (autoRes.ok) {
          const d = await autoRes.json();
          setAutomations(d.automations || []);
        }
        if (profRes.ok) {
          const d = await profRes.json();
          setProfile(d.profile);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-[#6366F1] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-automations-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">My Automations</h1>
          <p className="text-[#8b8fa3] text-sm mt-1">
            Automations you've published or submitted for review
          </p>
        </div>
        {profile?.username && (
          <Link
            to={`/creators/${profile.username}`}
            className="flex items-center gap-2 px-4 py-2 rounded-lg glass text-xs text-[#8b8fa3] hover:text-white transition-colors"
            data-testid="view-public-profile"
          >
            <Eye size={14} /> View Public Profile
          </Link>
        )}
      </div>

      {/* Stats Bar */}
      {profile && (
        <div className="grid grid-cols-3 gap-4" data-testid="creator-stats-bar">
          <div className="glass rounded-xl p-4">
            <p className="text-xs text-[#8b8fa3] mb-1">Total Published</p>
            <p className="text-xl font-bold text-white">{profile.total_automations || 0}</p>
          </div>
          <div className="glass rounded-xl p-4">
            <p className="text-xs text-[#8b8fa3] mb-1">Total Installs</p>
            <p className="text-xl font-bold text-white">{(profile.total_installs || 0).toLocaleString()}</p>
          </div>
          <div className="glass rounded-xl p-4">
            <p className="text-xs text-[#8b8fa3] mb-1">Total Runs</p>
            <p className="text-xl font-bold text-white">{(profile.total_runs || 0).toLocaleString()}</p>
          </div>
        </div>
      )}

      {/* Automations List */}
      {automations.length === 0 ? (
        <div className="glass rounded-2xl p-12 text-center" data-testid="no-automations">
          <Robot size={48} className="text-[#555] mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">No automations yet</h3>
          <p className="text-[#8b8fa3] text-sm mb-6">Build an agent and publish it to share with the community</p>
          <Link
            to="/unified-agent-builder"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white text-sm font-medium hover:opacity-90 transition-opacity"
          >
            <Robot size={16} /> Build an Agent
          </Link>
        </div>
      ) : (
        <div className="space-y-3" data-testid="automations-list">
          {automations.map(a => {
            const statusCfg = STATUS_CONFIG[a.review_status] || STATUS_CONFIG.draft;
            const StatusIcon = statusCfg.icon;
            return (
              <div key={a.id} className="glass rounded-xl p-4 flex items-center gap-4" data-testid={`my-automation-${a.id}`}>
                {/* Icon */}
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${a.color || '#6366F1'}20` }}
                >
                  <TemplateIcon icon={a.icon} size={20} className="text-indigo-400" />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <h3 className="text-white font-medium text-sm truncate">{a.name}</h3>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${statusCfg.color} ${statusCfg.bg}`}>
                      <StatusIcon size={10} />
                      {statusCfg.label}
                    </span>
                  </div>
                  <p className="text-[#8b8fa3] text-xs truncate">{a.tagline || a.short_description || a.category}</p>
                </div>

                {/* Stats */}
                <div className="flex items-center gap-4 text-xs text-[#6b7080] shrink-0">
                  <span className="flex items-center gap-1" title="Installs"><DownloadSimple size={12} /> {a.install_count || 0}</span>
                  <span className="flex items-center gap-1" title="Runs"><Play size={12} /> {a.run_count || a.usage_count || 0}</span>
                </div>

                {/* Date + Link */}
                <div className="text-right shrink-0">
                  <p className="text-[10px] text-[#555] mb-1">
                    {a.created_at ? new Date(a.created_at).toLocaleDateString() : '—'}
                  </p>
                  {a.review_status === 'approved' && a.slug && (
                    <Link to={`/automations/${a.slug}`} className="text-[10px] text-[#6366F1] hover:underline flex items-center gap-1 justify-end">
                      <ArrowSquareOut size={10} /> View
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyAutomations;
